import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { vendorCourierApi } from '@/services/vendorCourierApi';

const VendorCourierFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { courierId } = useParams<{ courierId: string }>();
  const isEditMode = Boolean(courierId);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    status: 'active',
  });

  useEffect(() => {
    const loadCourier = async () => {
      if (!courierId) {
        return;
      }

      setLoading(true);
      try {
        const courier = await vendorCourierApi.getCourier(courierId);
        setFormData({
          firstName: courier.firstName || '',
          lastName: courier.lastName || '',
          email: courier.email || '',
          phone: courier.phone || '',
          password: '',
          status: courier.status || 'active',
        });
      } catch (error: any) {
        console.error('Vendor kurye yükleme hatası:', error);
        toast.error(error.message || 'Kurye yüklenemedi');
        navigate('/vendor/couriers');
      } finally {
        setLoading(false);
      }
    };

    void loadCourier();
  }, [courierId, navigate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.phone || (!isEditMode && (!formData.email || !formData.password))) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode && courierId) {
        await vendorCourierApi.updateCourier(courierId, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          status: formData.status as 'active' | 'inactive',
          password: formData.password,
        });
        toast.success('Kurye güncellendi');
      } else {
        await vendorCourierApi.createCourier({
          ...formData,
          status: 'active',
        });
        toast.success('Kurye oluşturuldu');
      }
      navigate('/vendor/couriers');
    } catch (error: any) {
      console.error('Vendor kurye oluşturma hatası:', error);
      toast.error(error.message || (isEditMode ? 'Kurye güncellenemedi' : 'Kurye oluşturulamadı'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="animate-spin text-4xl">⏳</div></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/vendor/couriers')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">{isEditMode ? 'Kurye Düzenle' : 'Yeni Kurye'}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{isEditMode ? 'Kurye bilgilerini ve şifresini güncelleyebilirsiniz' : 'Bu kurye doğrudan sizin hesabınıza bağlanır'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Ad</label>
            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Soyad</label>
            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">E-posta</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={isEditMode} className="input-field disabled:opacity-70" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Telefon</label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-field" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">{isEditMode ? 'Yeni Şifre' : 'Şifre'}</label>
            <input type="password" name="password" minLength={6} value={formData.password} onChange={handleChange} className="input-field" />
            {isEditMode && <p className="text-xs text-gray-500 mt-1">Boş bırakırsanız mevcut şifre değişmez.</p>}
          </div>
          {isEditMode && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Durum</label>
              <select name="status" value={formData.status} onChange={handleChange} className="input-field">
                <option value="active">Aktif</option>
                <option value="inactive">İnaktif</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-60">
            <Save size={18} />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VendorCourierFormPage;